import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import InfiniteScroll from 'react-infinite-scroller';
import usePagination from '~utils/hooks/usePagination';
import { postQuizContactForm } from '~src/api/post';
import GreyTable from '~components/GreyTable';
import ObjectDetailsWithGalleryAndPreviewButtons from '~src/components/ObjectDetailsWithGalleryAndPreviewButtons';
import QuizHeading from '~components/ContactForm/components/QuizHeading';
import grammar from '~constants/grammar';
import ObjectTile from '~components/ObjectTile';
import ListingSelect from './components/ListingSelect';
import ListingSwitch, { Option } from './components/ListingSwitch';
import ContactFormBlock from '~components/ContactFormBlock';
import { ContactFormAbsoluteWrapper } from '~components/styledComponents';
import sortObjectsByField from '~utils/sortObjectsByField';
import isObject from 'lodash/isObject';
import { widthContextData } from '~contextProviders/WidthContextProvider';
import { currencyContextData } from '~contextProviders/CurrencyContextProvider';
import currencyMap from '~constants/currencyMap';
import { ObjectType } from '~entities/Object';
import { Query } from '~src/api/get/getObjects';
import { SortingDirectionsType } from '~src/types';
import { CurrencyType } from '~constants/currencyMap';
import { Nullable } from '~src/types';

import useSorting from '~utils/hooks/useSorting';
import getTableConfigsForObjects from '~utils/getTableConfigsForObjects';

import styles from './styles.module.css';
import { useHistory } from 'react-router-dom';
import { COMPONENT_IDS } from '~src/constants';
import { getUiIds, uiIdsConcat } from '~contextProviders/uiIds/helpers';
import { ComponentIdContextProvider } from '~contextProviders/uiIds/ComponentIdContextProvider';
import { ShowMoreButton, ShowMoreButtonWrapper } from "~components/Table/styledComponents";

type SortingValue =
  | keyof ObjectType
  | {
    ruble: 'cost_r' | 'costm_r';
    dollar: 'cost_d' | 'costm_d';
  };

type CommonConfig = {
  timeoutOfficesUpdate: number;
  timeoutFiltersUpdate: number;
  pageSize: number;
  showTextBefore: boolean;
  textBefore: string;
  types: Record<string, string>;
  sorting: {
    direction: SortingDirectionsType;
    value: SortingValue;
    options: Option<SortingValue>[];
  };
  views: Option<'grid' | 'list'>[];
};

type CatalogConfig = {
  category: Nullable<Query['typeListing']>;
  currency: CurrencyType;
  query: Query;
  list: string[];
  type:
  | 'catalogByQuery'
  | 'listingByQuery'
  | 'listingByIds'
  | 'favorite'
  | 'catalog';
  view: 'grid' | 'list';
  sortIrrelevantProducts: boolean;
  splitByGroups: boolean;
  visible: boolean;
  pagination: boolean;
  emptyMessageTitle: string;
  emptyMessageDesc: string;
};

export type Props = {
  config: { catalog: CatalogConfig; common: CommonConfig };
  items: ObjectType[];
  additionalContactFormPostFields?: Record<string, unknown>;
  noItemsComponent?: React.ReactNode;
  objectLinkHost?: string;
  typeListing: ObjectType['type_listing'];
  showPreloader: boolean;
  className?: string;
  openLinkInNewTab?: boolean;
  mainCalc?: boolean;
  totalArea?: { from: number; to: number } | undefined;
  category?: any;
  contactForm?: {
    show: boolean;
    afterSubmit?: () => void;
  };
  favorites?: {
    toggleFavorite: (id: ObjectType['id_crm']) => void;
    hasFavorites: (id: ObjectType['id_crm']) => boolean;
  };
};

const LISTING_MOUNT_ID = 'listing';

const Listing = ({
  config,
  items,
  className,
  additionalContactFormPostFields,
  noItemsComponent,
  objectLinkHost,
  openLinkInNewTab,
  typeListing,
  favorites,
  contactForm,
  mainCalc,
  totalArea,
}: Props) => {
  const componentId = COMPONENT_IDS.Listing;
  const uiIds = uiIdsConcat(getUiIds(), { componentId });

  const [calcShowItemSix, setCalcShowItemSix] = React.useState(true);
  let history = useHistory();
  React.useEffect(() => {
    window.innerWidth < 900
      ? setCalcShowItemSix(false)
      : setCalcShowItemSix(true);
  }, []);
  const { isMd, isLg, isXs } = widthContextData.useCtx();
  const {
    catalog: {
      view,
      category,
      splitByGroups,
      type,
      emptyMessageDesc,
      emptyMessageTitle,
      sortIrrelevantProducts,
    },
    common: { sorting, pageSize, views },
  } = config;
  const { setCurrency, currency } = currencyContextData.useCtx();

  const [showContactForm, setShowContactForm] = useState(
    !!contactForm && contactForm.show,
  );
  const [gridView, setGridView] = useState(view);

  const { current: listingContainerNode } = useRef(
    typeof document !== 'undefined' ? document.getElementById(LISTING_MOUNT_ID) : null,
  );

  const defaultSortField = isObject(sorting.value)
    ? sorting.value[currency]
    : sorting.value;

  const { sortingDirection, sortField, setSortField, sortByField } = useSorting<
    keyof ObjectType
  >(defaultSortField, 'asc');

  let sortedObjects = [...items];

  const group =
    splitByGroups ||
    /* type !== 'search' || type !== 'searchByQuery' || */ type !== 'catalog';

  if (group && category && category !== 'all') {
    sortedObjects = items.filter((item) => item['type_listing'] === category);
  }

  sortedObjects = sortObjectsByField(
    sortedObjects,
    sortField,
    sortingDirection,
  );

  if (sortIrrelevantProducts) {
    sortedObjects = sortObjectsByField(sortedObjects, 'realised', 'desc');
  }

  // move it to a separate component to avoid unnecessary rerenders
  const { paginatedData, hasMoreItems, showMore, shownItemsCount } = usePagination(
    sortedObjects,
    9,
  );

  const paginatedDataCheck = !mainCalc
    ? paginatedData
    : calcShowItemSix
      ? paginatedData.slice(0, 6)
      : paginatedData.slice(0, 3);

  const {
    mobileConfig,
    tabletConfig,
    desktopConfig,
  } = getTableConfigsForObjects({
    currency,
    favoriteButtonProps: favorites,
    typeListing,
  });

  let tableConfig;

  if (isXs) {
    tableConfig = mobileConfig;
  } else if (isMd || isLg) {
    tableConfig = tabletConfig;
  } else {
    tableConfig = desktopConfig;
  }

  const currencySelectOptions: Option<keyof typeof currencyMap>[] = [];

  (Object.keys(currencyMap) as (keyof typeof currencyMap)[]).forEach(
    (curencyKey) => {
      const cuyrrencyData = currencyMap[curencyKey];

      currencySelectOptions.push({
        value: curencyKey,
        label: cuyrrencyData.label,
      });
    },
  );

  const onCurrencyChange = (value: CurrencyType) => {
    if (sortField === 'cost_r' && value === 'dollar') {
      setSortField('cost_d');
    }
    if (sortField === 'cost_d' && value === 'ruble') {
      setSortField('cost_r');
    }
    if (sortField === 'costm_r' && value === 'dollar') {
      setSortField('costm_d');
    }
    if (sortField === 'costm_d' && value === 'ruble') {
      setSortField('costm_r');
    }

    setCurrency(value);
  };

  const afterContactFormSubmit = () => {
    setShowContactForm(false);

    if (contactForm && contactForm.afterSubmit) contactForm.afterSubmit();
  };

  const emptyListing = (
    <>
      <h2>{emptyMessageTitle}</h2>
      <p dangerouslySetInnerHTML={{ __html: emptyMessageDesc }} />
    </>
  );

  const classes = [styles['listing']];
  if (className) classes.push(className);

  const listing = (
    <ComponentIdContextProvider componentId={componentId}>
      <div className={classes.join(' ')}>
        {items.length ? (
          <div
            className={[
              styles['listing__content'],
              showContactForm ? styles['listing__content--blurred'] : undefined,
            ].join(' ')}
          >
            {!mainCalc && (
              <div className={styles['listing__head']}>
                <h1
                  className={[
                    styles['listing__title'],
                    styles['listing__head-item'],
                  ].join(' ')}
                >
                  {items.length} {grammar.offer(items.length)}
                </h1>
                <ListingSelect
                  currency={currency}
                  value={sortField}
                  direction={sortingDirection}
                  onChange={sortByField}
                  options={sorting.options}
                  className={styles['listing__head-item']}
                />
                <ListingSwitch
                  value={currency}
                  onChange={onCurrencyChange}
                  options={currencySelectOptions}
                  className={styles['listing__head-item']}
                />
                <ListingSwitch
                  value={gridView}
                  onChange={setGridView}
                  options={views}
                  className={styles['listing__head-item']}
                />
              </div>
            )}

            {gridView === 'grid' ? (
              <div>
                <InfiniteScroll
                  className={styles['listing__grid']}
                  loadMore={() => {}}
                >
                  {paginatedDataCheck.map((item) => {
                    const favoritesProp = favorites && {
                      toggleFavorite: () =>
                        favorites.toggleFavorite(item.id_crm),
                      isFavorite: favorites.hasFavorites(item.id_crm),
                    };

                    return (
                      <ObjectTile
                        key={item.id_crm}
                        className={styles['listing__grid-item']}
                        objectLinkHost={objectLinkHost}
                        openLinkInNewTab={openLinkInNewTab}
                        objectData={item}
                        currency={currency}
                        favorites={favoritesProp}
                      />
                    );
                  })}
                </InfiniteScroll>
                {hasMoreItems && (
                    <ShowMoreButtonWrapper>
                      <ShowMoreButton onClick={showMore}>
                        {shownItemsCount} из {items.length} показать ещё
                      </ShowMoreButton>
                    </ShowMoreButtonWrapper>
                )}
              </div>
            ) : (
              <div className={styles['listing__table']}>
                <GreyTable
                  getId={(data) => data.id_crm}
                  config={tableConfig}
                  renderExpandedComponent={(data) => (
                    <ObjectDetailsWithGalleryAndPreviewButtons
                      favorites={
                        favorites && {
                          isInFavorites: favorites.hasFavorites(data.id_crm),
                          toggleFavorite: () =>
                            favorites.toggleFavorite(data.id_crm),
                        }
                      }
                      data={{
                        id_crm: data.id_crm,
                        url: data.url,
                        name: data.name,
                        type_listing: data.type_listing,
                        address: data.address,
                        furnish: data.furnish,
                        furniture: data.furniture,
                        ceil: data.ceil,
                        view_to: data.view_to,
                        type: data.type,
                      }}
                      bottomButton='objectPageLink'
                    />
                  )}
                  data={sortedObjects}
                  itemsPerPage={pageSize}
                  sortField={sortField}
                  sortingDirection={sortingDirection}
                  onSortBtnClick={sortByField}
                />
              </div>
            )}
          </div>
        ) : (
          <div className={styles['listing__empty']}>
            {noItemsComponent || emptyListing}
          </div>
        )}
        {mainCalc && !showContactForm && (
          <div className={styles['listing__btn-inner']}>
            <div
              className={styles['listing__btn']}
              onClick={() =>
                history.push('/search', {
                  areaFrom: totalArea && totalArea.from,
                  areaTo: totalArea && totalArea.to,
                  currencyId: 1,
                  type: 'office',
                  typeListing: 'rent',
                })
              }
            >
              Результаты подбора: {items.length} предложений
            </div>
          </div>
        )}

        {!!items.length && showContactForm && (
          <ContactFormAbsoluteWrapper>
            <ContactFormBlock
              totalArea={totalArea}
              submitButtonLabel='Показать'
              afterSubmit={afterContactFormSubmit}
              heading={<QuizHeading offersLength={items.length} />}
              postFn={(formData) =>
                postQuizContactForm({
                  data: {
                    ...formData,
                    ...additionalContactFormPostFields,
                    uiIds,
                  },
                })
              }
              fieldNames={['phone', 'email']}
            />
          </ContactFormAbsoluteWrapper>
        )}
      </div>
    </ComponentIdContextProvider>
  );

  if (listingContainerNode) {
    return ReactDOM.createPortal(listing, listingContainerNode);
  }

  return listing;
};

export default Listing;
